import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Label } from '@/components/ui/label.tsx';
import { Checkbox } from '@/components/ui/checkbox.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.tsx';
import { useToast } from '@/hooks/use-toast.ts';
import { Loader2, Check, ChevronsUpDown, X, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext.tsx';
import { cn, cleanFormData, getErrorMessage, ErrorMessages } from '@/lib/utils.ts';
import { serviceFactory } from '@/services/core/serviceFactory.ts';
import { PatientEnrollerService, UpdatePatientEnrollerPayload } from '@/services/features/PatientEnrollerService.ts';
import { DropdownDataService, Clinic as ServiceClinic } from '@/services/features/DropdownDataService.ts';

interface Clinic {
  clinic_id: number;
  clinic_name: string;
  patient_clinic_timezone: string;
  has_active_enroller: boolean;
}

interface PatientEnroller {
  patient_enroller_id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  clinic_names: string;
  clinic_ids: number[];
  deferred_task: boolean;
  follow_up: boolean;
}

interface EditPatientEnrollerPESDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientEnroller: PatientEnroller | null;
  onSave: () => void;
}

const EditPatientEnrollerPESDialog: React.FC<EditPatientEnrollerPESDialogProps> = ({
  open,
  onOpenChange,
  patientEnroller,
  onSave,
}) => {
  const [formData, setFormData] = useState<PatientEnroller>({
    patient_enroller_id: 0,
    first_name: '',
    last_name: '',
    full_name: '',
    email: '',
    clinic_names: '',
    clinic_ids: [],
    deferred_task: false,
    follow_up: false,
  });
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [selectedClinicIds, setSelectedClinicIds] = useState<number[]>([]);
  const [clinicSearchOpen, setClinicSearchOpen] = useState(false);
  const [clinicSearch, setClinicSearch] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { toast } = useToast();
  const { user, token, hasuraToken } = useAuth();

  useEffect(() => {
    if (open && patientEnroller) {
      setFormData(patientEnroller);
      setSelectedClinicIds(patientEnroller.clinic_ids || []);
      setPassword('');
      setConfirmPassword('');
      setErrors({});
      
      if (hasuraToken) {
        fetchClinics();
      }
    }
  }, [open, patientEnroller, hasuraToken]);

  const fetchClinics = async () => {
    if (!hasuraToken) {
      toast({
        title: "Error",
        description: "Authentication token not available. Please sign in again.",
        variant: "destructive",
      });
      return;
    }

    try {
      const dropdownService = serviceFactory.createService(DropdownDataService, {
        hasuraToken
      });

      const result = await dropdownService.fetchAvailableClinics();
      
      if (result.success && result.data?.pep_vw_available_clinics) {
        // Convert ServiceClinic to local Clinic interface
        const clinicsData = result.data.pep_vw_available_clinics.map((clinic: ServiceClinic) => ({
          ...clinic,
          has_active_enroller: false // Default value since service doesn't provide this
        }));
        setClinics(clinicsData);
      } else if (result.errors) {
        console.error('GraphQL errors:', result.errors);
        toast({
          title: "Error",
          description: "Failed to load clinics - authentication error",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error fetching clinics:', error);
      toast({
        title: "Error",
        description: "Failed to load clinics",
        variant: "destructive",
      });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Check if any changes were made
    if (patientEnroller) {
      const originalClinicIds = patientEnroller.clinic_ids || [];
      const hasChanges = (
        formData.full_name.trim() !== patientEnroller.full_name ||
        formData.email.trim() !== patientEnroller.email ||
        formData.deferred_task !== patientEnroller.deferred_task ||
        formData.follow_up !== patientEnroller.follow_up ||
        password.trim() !== '' ||
        selectedClinicIds.length !== originalClinicIds.length ||
        !selectedClinicIds.every(id => originalClinicIds.includes(id))
      );

      if (!hasChanges) {
        toast({
          title: "Validation Error",
          description: "Please add details and update",
          variant: "destructive"
        });
        return false;
      }
    }

    if (!formData.full_name.trim()) {
      newErrors.full_name = ErrorMessages.REQUIRED_FIELD;
    }

    if (!formData.email.trim()) {
      newErrors.email = ErrorMessages.REQUIRED_FIELD;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = ErrorMessages.INVALID_EMAIL;
      }
    }

    if (password.trim() && password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (password.trim() && password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (selectedClinicIds.length === 0) {
      newErrors.clinics = 'Please select at least one clinic';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClinicToggle = (clinicId: number) => {
    setSelectedClinicIds(prev => 
      prev.includes(clinicId)
        ? prev.filter(id => id !== clinicId)
        : [...prev, clinicId]
    );
    // Clear clinic error when user selects a clinic
    if (errors.clinics) {
      setErrors(prev => ({ ...prev, clinics: '' }));
    }
  };

  const removeSelectedClinic = (clinicId: number) => {
    setSelectedClinicIds(prev => prev.filter(id => id !== clinicId));
  };

  const filteredClinics = clinics.filter(clinic =>
    clinic.clinic_name.toLowerCase().includes(clinicSearch.toLowerCase())
  );

  const selectedClinics = clinics.filter(c => selectedClinicIds.includes(c.clinic_id));

  const handleSubmit = async () => {
    if (!user?.id || !token) {
      toast({
        title: "Authentication Error",
        description: ErrorMessages.SESSION_EXPIRED,
        variant: "destructive"
      });
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      // Parse first and last name from full name
      const nameParts = formData.full_name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Clean form data before sending
      const cleanedData = cleanFormData({
        firstName,
        lastName,
        email: formData.email,
      });

      // Prepare service payload
      const payload: UpdatePatientEnrollerPayload = {
        first_name: cleanedData.firstName,
        last_name: cleanedData.lastName,
        email: cleanedData.email,
        clinic_ids: selectedClinicIds,
        deferred_task: formData.deferred_task,
        follow_up_task: formData.follow_up
      };

      // Only include password if it's provided
      if (password.trim()) {
        payload.password = password;
      }

      const patientEnrollerService = serviceFactory.createService(PatientEnrollerService, {
        userId: user.id,
        token,
        hasuraToken
      });

      const result = await patientEnrollerService.updatePatientEnroller(
        formData.patient_enroller_id,
        payload
      );
      
      if (result.success) {
        toast({
          title: "Success",
          description: "Patient Enrollment Specialist updated successfully!",
        });
        onSave();
        onOpenChange(false);
      } else {
        throw new Error(result.message || 'Update failed');
      }
    } catch (error: any) {
      console.error('Error updating patient enroller:', error);
      toast({
        title: "Update Failed",
        description: error.message || "Unable to update specialist details. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-center text-gray-800">
            Update Patient Enrollment Specialist Details
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="first_name" className="text-sm font-medium text-gray-700">
                First Name
              </Label>
              <Input
                id="first_name"
                value={formData.full_name.trim().split(' ')[0] || ''}
                onChange={(e) => {
                  const firstName = e.target.value;
                  const lastName = formData.full_name.trim().split(' ').slice(1).join(' ') || '';
                  setFormData({ ...formData, full_name: `${firstName} ${lastName}`.trim() });
                  if (errors.full_name) setErrors(prev => ({ ...prev, full_name: '' }));
                }}
                placeholder="First Name"
                className={`w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500 ${errors.full_name ? 'border-red-500' : ''}`}
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="last_name" className="text-sm font-medium text-gray-700">
                Last Name
              </Label>
              <Input
                id="last_name"
                value={formData.full_name.trim().split(' ').slice(1).join(' ') || ''}
                onChange={(e) => {
                  const firstName = formData.full_name.trim().split(' ')[0] || '';
                  const lastName = e.target.value;
                  setFormData({ ...formData, full_name: `${firstName} ${lastName}`.trim() });
                  if (errors.full_name) setErrors(prev => ({ ...prev, full_name: '' }));
                }}
                placeholder="Last Name"
                className={`w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500 ${errors.full_name ? 'border-red-500' : ''}`}
                autoComplete="off"
              />
            </div>
            {errors.full_name && <div className="col-span-2"><p className="text-red-500 text-sm">{errors.full_name}</p></div>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium text-gray-700">
              Email Address
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => {
                setFormData({ ...formData, email: e.target.value });
                if (errors.email) setErrors(prev => ({ ...prev, email: '' }));
              }}
              placeholder="Email Address"
              className={`w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500 ${errors.email ? 'border-red-500' : ''}`}
              autoComplete="off"
            />
            {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-medium text-gray-700">Assigned Tasks</Label>
            <div className="flex space-x-6">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="deferred_task"
                  checked={formData.deferred_task || false}
                  onCheckedChange={(checked) => setFormData({ ...formData, deferred_task: checked as boolean })}
                />
                <Label htmlFor="deferred_task" className="text-sm text-gray-700">
                  Deferred
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="follow_up"
                  checked={formData.follow_up || false}
                  onCheckedChange={(checked) => setFormData({ ...formData, follow_up: checked as boolean })}
                />
                <Label htmlFor="follow_up" className="text-sm text-gray-700">
                  Follow Up
                </Label>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (errors.password) setErrors(prev => ({ ...prev, password: '' }));
                    }}
                    placeholder="Leave blank to keep current"
                    className={`w-full pr-10 rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500 ${errors.password ? 'border-red-500' : ''}`}
                    autoComplete="new-password"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 text-gray-500 hover:text-gray-700"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                {!errors.password && (
                  <p className="text-gray-500 text-xs">Leave blank to keep current password</p>
                )}
                {errors.password && <p className="text-red-500 text-sm">{errors.password}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
                  Confirm Password
                </Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (errors.confirmPassword) setErrors(prev => ({ ...prev, confirmPassword: '' }));
                    }}
                    placeholder="Confirm Password"
                    className={`w-full pr-10 rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500 ${errors.confirmPassword ? 'border-red-500' : ''}`}
                    autoComplete="new-password"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 text-gray-500 hover:text-gray-700"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                {errors.confirmPassword && <p className="text-red-500 text-sm">{errors.confirmPassword}</p>}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="clinic">Select Clinic</Label>
            
            {/* Selected Clinics Display */}
            {selectedClinics.length > 0 && (
              <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-gray-50">
                {selectedClinics.map((clinic) => (
                  <div
                    key={clinic.clinic_id}
                    className="flex items-center space-x-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                  >
                    <span>{clinic.clinic_name}</span>
                    <button
                      type="button"
                      onClick={() => removeSelectedClinic(clinic.clinic_id)}
                      className="hover:bg-blue-200 rounded-full p-1"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <Popover open={clinicSearchOpen} onOpenChange={setClinicSearchOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={clinicSearchOpen}
                  className={`w-full justify-between rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500 ${errors.clinics ? 'border-red-500' : ''}`}
                  type="button"
                >
                  {selectedClinicIds.length > 0
                    ? `${selectedClinicIds.length} clinic(s) selected`
                    : "Select Clinic"}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent 
                className="w-[--radix-popover-trigger-width] p-0 bg-white border shadow-lg z-[9999] max-h-80"
                align="start"
                sideOffset={4}
              >
                <div className="p-3 border-b bg-white">
                  <Input
                    placeholder="Search clinics..."
                    value={clinicSearch}
                    onChange={(e) => setClinicSearch(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div className="max-h-60 overflow-y-auto p-2 bg-white">
                  {clinics.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">Loading clinics...</div>
                  ) : filteredClinics.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">No clinics found</div>
                  ) : (
                    [...filteredClinics]
                      .sort((a, b) => {
                        const aSelected = selectedClinicIds.includes(a.clinic_id);
                        const bSelected = selectedClinicIds.includes(b.clinic_id);
                        if (aSelected && !bSelected) return -1;
                        if (!aSelected && bSelected) return 1;
                        return a.clinic_name.localeCompare(b.clinic_name);
                      })
                      .map((clinic) => (
                        <div
                          key={clinic.clinic_id}
                          className={cn(
                            "flex items-center space-x-3 px-3 py-2 text-sm cursor-pointer rounded-md hover:bg-gray-100",
                            selectedClinicIds.includes(clinic.clinic_id) && "bg-blue-50 text-blue-700"
                          )}
                          onClick={() => handleClinicToggle(clinic.clinic_id)}
                        >
                          <div className={cn(
                            "w-4 h-4 border border-gray-300 rounded flex items-center justify-center",
                            selectedClinicIds.includes(clinic.clinic_id) && "bg-blue-600 border-blue-600"
                          )}>
                            {selectedClinicIds.includes(clinic.clinic_id) && (
                              <Check className="h-3 w-3 text-white" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="font-medium">{clinic.clinic_name}</div>
                            <div className="text-xs text-gray-500">{clinic.patient_clinic_timezone}</div>
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </PopoverContent>
            </Popover>
            {errors.clinics && <p className="text-red-500 text-sm">{errors.clinics}</p>}
          </div>
        </div>

        <DialogFooter className="flex justify-end space-x-3 pt-6">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
            className="px-6 py-2 rounded-lg border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium shadow-lg hover:shadow-xl transition-all duration-200"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Updating...
              </>
            ) : (
              "Update"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditPatientEnrollerPESDialog;